import { useState, useEffect, useCallback } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Zap, Loader2, TrendingUp, Building2 } from 'lucide-react';
import paytrLogo from '@/assets/paytr-logo.svg';

// Order = display order in the grid (left → right).
const PLAN_ORDER = ['randevu', 'tahsilat', 'tam', 'kurumsal'];
const POPULAR_KEY = 'tam';

// Per-plan visual identity. Tahsilat = amber; randevu = blue; tam = purple
// (premium feel); kurumsal = slate (enterprise). Picked from Tailwind's
// established families so contrast stays accessible without re-tuning.
const planColors = {
  randevu:   { bg: 'bg-blue-50',   border: 'border-blue-300',   badge: 'bg-blue-100 text-blue-700',     btn: 'bg-blue-600 hover:bg-blue-700' },
  tahsilat:  { bg: 'bg-amber-50',  border: 'border-amber-300',  badge: 'bg-amber-100 text-amber-800',   btn: 'bg-amber-600 hover:bg-amber-700' },
  tam:       { bg: 'bg-purple-50', border: 'border-purple-300', badge: 'bg-purple-100 text-purple-700', btn: 'bg-purple-600 hover:bg-purple-700' },
  kurumsal:  { bg: 'bg-slate-50',  border: 'border-slate-300',  badge: 'bg-slate-100 text-slate-700',   btn: 'bg-slate-800 hover:bg-slate-900' },
};

const fmtLimit = (n) => (n === null || n === undefined || n >= 999999 ? 'Sınırsız' : n.toLocaleString('tr-TR'));
const fmtPrice = (n) => (n == null ? null : n.toLocaleString('tr-TR'));

export const AdminPlanUpgrade = () => {
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const [planInfo, setPlanInfo] = useState(null);
  const [upgradingTo, setUpgradingTo] = useState(null);
  const [polling, setPolling] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const fetchPlanInfo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/plan/info`);
      setPlanInfo(data);
    } catch {
      toast.error('Plan bilgisi yüklenemedi');
    }
  }, [API_URL]);

  useEffect(() => { fetchPlanInfo(); }, [fetchPlanInfo]);

  // Detect return from PayTR
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('upgrade_session_id');
    if (sid) {
      setPolling(sid);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!polling) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/subscription/check/${polling}`);
        if (data.payment_status === 'paid') {
          toast.success(`Plan yükseltmesi başarılı! Yeni plan: ${data.new_plan || 'aktif'}`);
          setPolling(null);
          fetchPlanInfo();
          return;
        }
        if (data.status === 'expired') {
          toast.error('Ödeme süresi doldu');
          setPolling(null);
          return;
        }
        attempts++;
        if (attempts < 8) setTimeout(poll, 2000);
        else { toast.info('Durum kontrol ediliyor...'); setPolling(null); }
      } catch {
        setPolling(null);
      }
    };
    poll();
  }, [polling, API_URL, fetchPlanInfo]);

  const handleUpgrade = async (planKey) => {
    setUpgradingTo(planKey);
    try {
      const { data } = await axios.post(`${API_URL}/api/plan/upgrade`, {
        plan: planKey,
        billing_cycle: billingCycle,
        origin_url: window.location.origin,
      });
      if (data.url) {
        const titleParam = `${planInfo?.all_plans?.[planKey]?.label || 'Plan'} Yükseltme`;
        window.location.href = `/checkout?title=${encodeURIComponent(titleParam)}&return=${encodeURIComponent('/admin/plan-upgrade')}&url=${encodeURIComponent(data.url)}`;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yükseltme başlatılamadı');
      setUpgradingTo(null);
    }
  };

  const handleContactSales = () => {
    // Public booking page footer + support module both work; for now route to /support
    window.location.href = '/support?subject=Kurumsal+Teklif+Talebi';
  };

  if (!planInfo) {
    return (
      <PageLayout>
        <div className="text-center py-20 text-[#595959]">Yükleniyor...</div>
      </PageLayout>
    );
  }

  const { plan: currentPlan, all_plans } = planInfo;
  const orderedPlans = PLAN_ORDER.filter((k) => all_plans?.[k]);

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="upgrade-title">
            Planınızı Yükseltin
          </h1>
          <p className="text-base text-[#595959] mt-2">
            Mevcut plan: <strong>{planInfo.plan_label}</strong>
            {all_plans[currentPlan]?.monthly_price != null && (
              <> · Aylık ₺{fmtPrice(all_plans[currentPlan].monthly_price)}</>
            )}
          </p>
        </div>

        {polling && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <p className="text-sm text-blue-700 font-medium">Ödeme durumu kontrol ediliyor...</p>
          </div>
        )}

        {/* Billing cycle toggle */}
        <div className="flex justify-center mb-10 pt-3">
          <div className="inline-flex items-center gap-1 bg-white rounded-full p-1 border border-[#E5E5E5] shadow-sm relative" data-testid="billing-cycle-toggle">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition ${billingCycle === 'monthly' ? 'bg-[#2563EB] text-white' : 'text-[#595959] hover:text-[#2563EB]'}`}
              data-testid="billing-monthly"
            >
              Aylık
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition relative ${billingCycle === 'yearly' ? 'bg-[#2563EB] text-white' : 'text-[#595959] hover:text-[#2563EB]'}`}
              data-testid="billing-yearly"
            >
              Yıllık
            </button>
            <span className="absolute -top-3 right-0 translate-x-1/4 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow whitespace-nowrap">
              2 AY BEDAVA
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {orderedPlans.map((key) => {
            const plan = all_plans[key];
            const color = planColors[key] || planColors.randevu;
            const isCurrent = key === currentPlan;
            const isContactSales = !!plan.contact_sales;
            const isRecommended = key === POPULAR_KEY;
            return (
              <Card
                key={key}
                className={`relative border-2 ${color.border} ${color.bg} transition-transform hover:-translate-y-1 ${isCurrent ? 'ring-2 ring-offset-2 ring-[#2563EB]' : ''} ${isRecommended ? 'shadow-xl' : ''}`}
                data-testid={`plan-card-${key}`}
              >
                {isRecommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-purple-600 to-purple-500 text-white text-xs font-bold rounded-full shadow-md whitespace-nowrap" data-testid="plan-popular-badge">
                    EN POPÜLER
                  </div>
                )}
                <CardContent className="p-5 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-3 min-h-[24px]">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color.badge}`}>
                      {plan.label}
                    </span>
                    {isCurrent && (
                      <span className="text-[10px] font-bold text-[#2563EB] bg-white px-2 py-0.5 rounded-full border border-[#2563EB]/30" data-testid={`current-badge-${key}`}>
                        MEVCUT PLAN
                      </span>
                    )}
                  </div>

                  <div className="mb-4 min-h-[120px]">
                    {isContactSales ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-[#1A1A2E] tabular-nums">Teklif Al</span>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-[#1A1A2E] tabular-nums">
                          ₺{billingCycle === 'yearly'
                            ? fmtPrice(plan.yearly_price || plan.monthly_price * 10)
                            : fmtPrice(plan.monthly_price)}
                        </span>
                        <span className="text-sm text-[#595959]">/{billingCycle === 'yearly' ? 'yıl' : 'ay'}</span>
                      </div>
                    )}
                    {!isContactSales && billingCycle === 'yearly' && plan.monthly_price && (
                      <p className="text-xs text-emerald-700 font-semibold mt-1">
                        ₺{fmtPrice(plan.monthly_price)}/ay × 10 ay = 2 ay bedava
                      </p>
                    )}
                    {plan.tagline && (
                      <p className="text-xs text-[#595959] mt-2 leading-snug">{plan.tagline}</p>
                    )}
                    {plan.suitable_for && (
                      <p className="text-[11px] text-[#94A3B8] mt-2 italic leading-snug">{plan.suitable_for}</p>
                    )}
                  </div>

                  <div className="space-y-1.5 mb-5 text-sm flex-1">
                    <div className="flex items-center gap-2 text-[#1A1A2E]">
                      <Building2 className="w-4 h-4 text-[#2563EB] shrink-0" />
                      <span><strong>{fmtLimit(plan.max_customers)}</strong> müşteri/üye</span>
                    </div>
                    {(plan.features || []).map((f, i) => (
                      <div key={i} className="flex items-start gap-2 text-[#475569] text-xs leading-snug">
                        <Check className="w-3.5 h-3.5 text-[#16A34A] shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>

                  {isContactSales ? (
                    <Button
                      onClick={handleContactSales}
                      className={`w-full h-11 font-semibold text-white ${color.btn}`}
                      data-testid={`contact-sales-${key}`}
                    >
                      Teklif Al
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleUpgrade(key)}
                      disabled={upgradingTo !== null}
                      className={`w-full h-11 font-semibold text-white ${color.btn}`}
                      data-testid={`upgrade-btn-${key}`}
                    >
                      {upgradingTo === key ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Yönlendiriliyor...</>
                      ) : isCurrent ? (
                        <><Zap className="w-4 h-4 mr-1" /> Bu Planı Satın Al</>
                      ) : (
                        <><TrendingUp className="w-4 h-4 mr-1" /> Bu Planı Seç</>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="flex items-center justify-center gap-2 mt-6" data-testid="upgrade-disclaimer">
          <p className="text-xs text-[#64748B] text-center">
            Ödeme kartla güvenli ödeme altyapısı üzerinden yapılır. Plan anında aktifleşir ve sözleşmeniz 30 gün uzatılır.
          </p>
        </div>
        <div className="flex items-center justify-center gap-1.5 mt-3">
          <span className="text-[11px] text-[#94A3B8]">Powered by</span>
          <img src={paytrLogo} alt="PayTR" className="h-4 opacity-90" data-testid="paytr-logo" />
        </div>

        <div className="mt-4 mx-auto max-w-3xl rounded-lg border border-[#E5E5E5] bg-[#FAFAF8] px-4 py-3" data-testid="payment-institution-disclaimer">
          <p className="text-[11px] leading-relaxed text-[#595959] text-center">
            Paydaai bir ödeme kuruluşu değildir. Platform üzerinden yapılan ödemeler ilgili işletmelere aktarılır. Ödeme işlemleri sırasında oluşabilecek komisyon ve hizmet bedelleri, ilgili ödeme kuruluşları tarafından belirlenmektedir.
          </p>
        </div>
      </div>
    </PageLayout>
  );
};
